import {
  MiddlewareAPI,
  isRejectedWithValue,
  Middleware,
} from '@reduxjs/toolkit';
import { logout } from '../redux/userSlice';
import { clearImpersonation } from '../redux/authorizationSlice';
import { setMessage } from '../redux/messageSlice';
import { AuthErrorHandler } from './authErrorHandler';
import {
  AuthErrorResponse,
  ERROR_CODES,
} from './errorStructure';
import { getErrorMessageKey, isAuthError } from './errorMapper';
import i18n from '../i18n/config';
import { userApi } from '../services/user';

export const rtkQueryErrorLogger: Middleware =
  function (api: MiddlewareAPI) {
    return function (next) {
      return function (action) {
        if (isGetUserProfileDeactivatedRejection(action)) {
          const payload = (action as Record<string, unknown>).payload;
          if (handleClientProfileDeactivated(api, payload)) {
            return next(action);
          }
        }

        if (isRejectedWithValue(action)) {
          const baseQueryMeta = (action.meta as Record<string, unknown>)
            ?.baseQueryMeta as Record<string, unknown> | undefined;
          const requestUrl = (baseQueryMeta?.request as Request | undefined)?.url || '';
          const endpoint = extractEndpoint(requestUrl);

          const authErrorHandler = new AuthErrorHandler(api);

          if (isAuthEndpoint(endpoint)) {
            handleAuthError(action.payload, authErrorHandler, endpoint);
          } else {
            handleGenericError(action, api, endpoint);
          }
        }

        return next(action);
      };
    };
  };

function extractEndpoint(url: string): string {
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;

    let endpoint = path.startsWith('/') ? path.slice(1) : path;
    if (endpoint.startsWith('api/')) {
      endpoint = endpoint.slice(4);
    }

    return endpoint;
  } catch {
    return url.split('bsx-group.com')[1]?.slice(1) || url;
  }
}

function isAuthEndpoint(endpoint: string): boolean {
  return (
    endpoint.startsWith('auth/') ||
    endpoint.startsWith('v1/auth/') ||
    endpoint.includes('sign-in') ||
    endpoint.includes('sign-up') ||
    endpoint.includes('logout')
  );
}

const FIELD_LEVEL_ERROR_CODES = new Set([
  'PRODUCT_SKU_DUPLICATE',
]);

function shouldSkipGlobalErrorHandling(endpoint: string, action?: any): boolean {
  // Download endpoints handle errors locally — skip global toast
  if (endpoint.includes('/attachments/') && endpoint.endsWith('/download')) return true;

  // Field-level errors are handled locally by the form — skip global toast
  const errorCode = action?.payload?.data?.error?.code;
  if (errorCode && FIELD_LEVEL_ERROR_CODES.has(errorCode)) return true;

  return false;
}

function handleAuthError(action: any, authErrorHandler: AuthErrorHandler, endpoint: string): void {
  const error = action.data as AuthErrorResponse;
  const errorCode = error?.error?.code;

  // The client sign-in form owns this error because it displays the
  // server-provided cooldown in a parameterized message.
  if (
    (endpoint === 'v1/auth/client/sign-in' || endpoint === 'auth/client/sign-in') &&
    errorCode === 'OTP_RESEND_TOO_SOON'
  ) {
    return;
  }

  console.log('API ErrorhandleAuthError', {
    endpoint,
    error,
    code: errorCode,
    details: error?.error?.details,
  });

  switch (endpoint) {
    case 'v1/auth/sign-in':
    case 'auth/sign-in':
      authErrorHandler.handleSignInError(error);
      break;

    case 'v1/auth/client/sign-in':
    case 'auth/client/sign-in':
      authErrorHandler.handleSignInError(error);
      break;

    case 'v1/auth/sign-up':
      authErrorHandler.handleSignUpError(error);
      break;

    case 'v1/auth/sign-up/user':
    case 'v1/auth/sign-up/company':
    case 'v1/auth/sign-up/complete':
      // Handled locally in useSignUp — skip global toast
      break;

    case 'v1/auth/logout':
      authErrorHandler.handleLogoutError(error);
      break;

    case 'v1/auth/reset-password':
    case 'auth/reset-password':
    case 'v1/auth/forgot-password':
    case 'auth/forgot-password':
      authErrorHandler.handleGenericAuthError(error);
      break;

    default:
      if (error.error.statusCode === ERROR_CODES.UNAUTHORIZED) {
        authErrorHandler.handleSignInError(error);
      } else {
        authErrorHandler.handleGenericAuthError(error);
      }
  }
}

const PROFILE_DEACTIVATED_CODE = 'CLIENT_USER_DEACTIVATED';

function isGetUserProfileDeactivatedRejection(action: unknown): boolean {
  if (!userApi.endpoints.getUser.matchRejected(action)) return false;
  const a = action as Record<string, unknown>;
  if ((a.meta as Record<string, unknown> | undefined)?.aborted) return false;
  return !!a.payload;
}

function isUsersProfileEndpoint(endpoint: string): boolean {
  return endpoint === 'v1/users/profile' || endpoint === 'users/profile';
}

/**
 * RTK/fetchBaseQuery: payload.data is usually the JSON body.
 * Backend envelope: { status, error: { code, domain, message, statusCode } } or Nest-style flat fields.
 */
function getRejectedErrorBlock(
  payload: unknown,
): { statusCode?: number; code?: string } | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const p = payload as Record<string, unknown>;
  const data = p.data;
  let err: Record<string, unknown> | undefined;

  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    const nested = d.error;
    if (nested && typeof nested === 'object') {
      err = nested as Record<string, unknown>;
    } else if (typeof d.code === 'string') {
      err = d;
    }
  }

  if (!err) {
    const top = p.error;
    if (top && typeof top === 'object') err = top as Record<string, unknown>;
  }

  if (!err || typeof err !== 'object') return undefined;

  const rawStatus = err.statusCode ?? p.status;
  const statusCode =
    rawStatus != null && Number.isFinite(Number(rawStatus))
      ? Number(rawStatus)
      : undefined;
  const code = typeof err.code === 'string' ? err.code : undefined;

  if (statusCode === undefined && code === undefined) return undefined;
  return { statusCode, code };
}

function handleClientProfileDeactivated(api: MiddlewareAPI, payload: unknown): boolean {
  const block = getRejectedErrorBlock(payload);
  if (block?.statusCode !== 403 || block?.code !== PROFILE_DEACTIVATED_CODE) {
    return false;
  }

  const p = payload as Record<string, unknown>;
  const data = p.data as Record<string, unknown> | undefined;
  const nestedErr =
    data?.error && typeof data.error === 'object'
      ? (data.error as Record<string, unknown>)
      : undefined;
  const msg =
    (typeof nestedErr?.message === 'string' && nestedErr.message) ||
    (typeof data?.message === 'string' && data.message) ||
    i18n.t('error:client.CLIENT_USER_DEACTIVATED', {
      defaultValue: 'Your account access has ended. Please sign in again.',
    });

  api.dispatch(
    setMessage({
      value: msg,
      timeout: 8000,
      type: 'error',
    }),
  );

  api.dispatch(logout());
  api.dispatch(clearImpersonation());

  return true;
}

function handleGenericError(action: any, api: MiddlewareAPI, endpoint: string): void {
  if (shouldSkipGlobalErrorHandling(endpoint, action)) {
    return;
  }

  const payload = action?.payload;

  if (isUsersProfileEndpoint(endpoint)) {
    if (handleClientProfileDeactivated(api, payload)) {
      return;
    }
  }

  if (isAuthError(payload)) {
    api.dispatch(logout());
    api.dispatch(clearImpersonation());
    return;
  }

  const errorData = action?.payload?.data?.error || action?.payload?.data;

  if (errorData?.code && errorData?.domain) {
    const errorKey = getErrorMessageKey(action?.payload);

    const translated = i18n.t(`error:${errorKey}`, {
      defaultValue: null,
    });

    const message =
      translated ||
      errorData?.details ||
      errorData?.message ||
      i18n.t('error:general.UNKNOWN_ERROR', {
        defaultValue: 'An error occurred. Please try again.',
      });

    api.dispatch(
      setMessage({
        value: message,
        timeout: 8000,
        type: 'error',
      }),
    );
    return;
  }

  let errors = Object.keys(action?.payload?.data?.errors || {});

  if (errors.length > 0) {
    errors.forEach((error) => {
      api.dispatch(
        setMessage({
          value: action?.payload?.data?.errors[error][0],
          timeout: 8000,
          type: 'error',
        }),
      );
    });
    return;
  }

  const fallbackMessage =
    errorData?.details ||
    errorData?.message ||
    i18n.t('error:general.DEFAULT', { defaultValue: 'Something went wrong' });

  api.dispatch(
    setMessage({
      value: fallbackMessage,
      timeout: 5000,
      type: 'error',
    }),
  );
}
